import {Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {RightPanel} from '../right-panel.class';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfigurationService} from '../../../../../configuration/configuration.service';
import {AssetsManagerService} from '../../../../../data/assets/assets-manager.service';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ToastService} from '../../../../../shared/services/toast.service';
import {RouteAssignmentService} from '../../../../../data/routes/route-assignment.service';
import {
  RouteAssignment,
  RouteAssignmentQueue,
  RouteAssignmentStatus,
  VehicleRouteAssignmentStatus
} from '../../../../../shared/models/route-assignment';
import {ActionMenuItem, ActionMenuItemSubMenu} from '../../../../../shared/models/action-menu-item.class';
import {
  RouteConfigurationWithSchema,
  RouteHierarchyItem,
  RouteHierarchyItemWithPath
} from '../../../../../shared/models/route';
import {RouteAssignmentManagerService} from '../../../../../data/routes/route-assignment-manager.service';
import {LiveMapDataService} from '../../../services/live-map-data.service';
import {MainRoute, RootRoute} from '../../../../../shared/models/angular-routing';
import {Asset} from '../../../models/asset.class';
import {DrawerContent} from '../../../../../layouts/right-drawer/right-drawer.component';
import moment from 'moment';
import {ShiftDevices} from '../../../../../data/websocket/model/message.event';
import {InsightsRoute, ShiftDetailRoute} from '../../../../insights/insights.routes';
import {DateFilter} from '../../../../../shared/models/DateFilter';
import {MultiSelectFilter} from '../../../../../shared/components/multi-select-component';
import {FilterBarComponent} from '../../../../../shared/components/filter-bar/filter-bar.component';
import {FilterButtonComponent} from '../../../../../shared/components/buttons/filter-button/filter-button.component';
import {MatIcon} from '@angular/material/icon';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatOptgroup, MatOption, MatSelect} from '@angular/material/select';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatButton, MatIconButton} from '@angular/material/button';
import {CardMenuItemComponent} from '../../../../../shared/components/card-menu-item/card-menu-item.component';
import {
  RouteAssignmentStatusItem
} from '../../../../../shared/components/route-assignment-status-filter/route-assignment-status-filter.component';
import { NgClass } from '@angular/common';

class AssignmentAction {
  constructor(
    readonly title: string,
    readonly onClick: () => void,
  ) {
  }
}

interface SingleCardSegment {
  type: 'single';
  key: string;
  item: ActionMenuItem;
}

interface CardGroupSegment {
  type: 'group';
  key: string;
  groupUuid: string;
  items: ActionMenuItem[];
}

type CardSegment = SingleCardSegment | CardGroupSegment;

export const LOCAL_STORAGE_ROUTE_ASSIGNMENT_VEHICLE_ID = 'route-assignment-predefined-vehicle-id';

@Component({
  selector: 'app-route-assignment',
  templateUrl: './route-assignment.component.html',
  styleUrls: ['./route-assignment.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatIcon,
    FilterButtonComponent,
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    MatOption,
    ReactiveFormsModule,
    MatOptgroup,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatButton,
    MatIconButton,
    FilterBarComponent,
    CardMenuItemComponent,
    NgClass,
  ],
})
export class RouteAssignmentComponent extends RightPanel implements OnInit, OnDestroy {
  routeAssignments: RouteAssignment[];
  filteredRouteAssignments: RouteAssignment[];
  segments: CardSegment[];
  assignmentActions: AssignmentAction[] = [];
  readonly vehiclesStatus: Map<number, VehicleRouteAssignmentStatus> = new Map<number, VehicleRouteAssignmentStatus>();

  assigningMode = false;
  showConfirmDialog = false;
  delayedRouteAction = false;

  flatRouteItems: RouteHierarchyItemWithPath[];
  routeHierarchyItems: RouteHierarchyItem[];

  routeSelectorControl: FormControl<RouteHierarchyItem[]> = new FormControl([]);

  availableVehiclesForAssignment: Asset[] = [];
  selectedVehicle: Asset;
  selectedRoutes: RouteHierarchyItem[];
  RouteAssignmentQueue = RouteAssignmentQueue;

  dateFilter: DateFilter;
  vehicleFilter: MultiSelectFilter<number>;
  routeAssignmentStatusFilter: MultiSelectFilter<string>;

  // workaround for https://github.com/angular/components/issues/27209
  private panelEl: HTMLElement | null = null;
  private savedScrollTop = 0;

  @ViewChild(FilterBarComponent) filterBar: FilterBarComponent;

  constructor(
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected configurationService: ConfigurationService,
    protected assetsManager: AssetsManagerService,
    private readonly routeAssignmentService: RouteAssignmentService,
    private readonly routeAssignmentManager: RouteAssignmentManagerService,
    private readonly liveMapDataService: LiveMapDataService,
    private readonly toast: ToastService
  ) {
    super(DrawerContent.ROUTE, router, activatedRoute, configurationService, assetsManager);
  }

  ngOnInit() {
    this.initialize();
    this.loadConfiguration();
    this.selectedVehicle = this.asset;

    const assignmentsSubscription = this.routeAssignmentManager.recentRouteAssignments$.subscribe(assignmentsUpdate => {
      // filter out these which are completed more than 24 hours ago
      const moment24housAgo = moment().subtract(24, 'hour');
      this.routeAssignments = RouteAssignment.sortByCreated(
        assignmentsUpdate.state.filter(
          assignment =>
            !assignment.completed ||
            moment(assignment.completed).isAfter(moment24housAgo) ||
            (assignment.declined && moment(assignment.declined).isAfter(moment24housAgo))
        )
      );
      this.updateUiItems();
    });
    this.openSubscriptions.push(assignmentsSubscription);

    const vehicleStatusSubscription = this.routeAssignmentManager.vehicleRouteAssignmentStatus$.subscribe(statusUpdate => {
      let vehiclesStatus: VehicleRouteAssignmentStatus[];
      if (statusUpdate.updated.length === 0) {
        // initial setup
        vehiclesStatus = statusUpdate.state;
      } else {
        // only update
        vehiclesStatus = statusUpdate.updated;
      }
      vehiclesStatus.forEach(vehicleStatus => {
        this.vehiclesStatus.set(vehicleStatus.vehicleId, vehicleStatus);
      });
      this.updateUiItems();
      this.onAssignmentFormChange();
    });
    this.openSubscriptions.push(vehicleStatusSubscription);

    const routeSubscription = this.liveMapDataService.routeConfiguration$.subscribe(routeConfiguration => {
      this.flatRouteItems = routeConfiguration
        .map(routeConfig => {
          return this.routeConfigToLeaves(routeConfig);
        })
        .flat();
      this.routeHierarchyItems = routeConfiguration.map(routeConfig => {
        return this.routeConfigToHierarchy(routeConfig);
      });
      if (!!this.routeConfigId && !!this.routeId && this.delayedRouteAction) {
        this.preFillRoute();
        this.delayedRouteAction = false;
      }
    });
    this.openSubscriptions.push(routeSubscription);

    this.routeSelectorControl.valueChanges.subscribe(routeHierarchyItems => {
      this.selectedRoutes = routeHierarchyItems;
      this.onAssignmentFormChange();
    });
  }

  ngOnDestroy() {
    this.unsubscribe();
    this.vehiclesStatus.clear();
  }

  onAssetsUpdate() {
    this.availableVehiclesForAssignment = this.assets
      .filter(asset => {
        return asset.shiftDevices !== ShiftDevices.LMU;
      })
      .sort((a, b) => {
        return a.name < b.name ? -1 : 1;
      });
  }

  onAssetChange() {
    this.selectedVehicle = this.asset;
    this.assigningMode = true;
    this.onAssignmentFormChange();
  }

  onAssetAction() {
    this.selectedVehicle = this.asset;
    this.onAssignmentFormChange();
    this.assigningMode = true;
  }

  onRouteAction() {
    if (!!this.routeConfigId && !!this.routeId && !!this.flatRouteItems) {
      this.preFillRoute();
    } else {
      // route items not ready at this moment
      this.delayedRouteAction = true;
    }
  }

  onConfigurationLoad() {
    // do nothing
  }

  onAssignmentFormChange() {
    this.prepareAssignmentActions();

    // undo Material's scroll jump on the next frame
    const panel = this.panelEl;
    const top = this.savedScrollTop;
    if (panel) {
      requestAnimationFrame(() => {
        panel.scrollTop = top;
      });
    }
  }

  toggleAssigningMode() {
    this.assigningMode = !this.assigningMode;
    this.routeSelectorControl.reset([]);
    this.selectedRoutes = null;
    this.selectedVehicle = null;
    this.showConfirmDialog = false;
  }

  async assignRoute(queueOrder: RouteAssignmentQueue, concurrent: boolean = false) {
    const assignments: RouteAssignment[] = [];
    this.selectedRoutes.forEach(selectedRoute => {
      const assignment = new RouteAssignment();
      assignment.configId = selectedRoute.configId;
      assignment.routeId = selectedRoute.routeId;
      assignment.routeName = selectedRoute.value;
      assignment.routeLength = selectedRoute.length ?? 0;
      assignment.vehicleId = this.selectedVehicle.id;
      assignment.shiftId = this.selectedVehicle.shiftId;
      assignments.push(assignment);
    });

    // UI validation
    assignments.forEach(assignment => {
      if (
        !this.routeAssignmentManager.canRouteBeAssigned(assignment.vehicleId, assignment.shiftId, assignment.configId, assignment.routeId)
      ) {
        this.toast.short(`Route '${assignment.routeName}' is already queued!`);
        return;
      }
    });

    try {
      if (assignments.length > 1 && concurrent) {
        // concurrent multiple routes assignment
        await this.routeAssignmentService.createRouteAssignment(assignments, queueOrder);
      } else {
        for (const assignment of assignments) {
          // one or more sequential assignments
          await this.routeAssignmentService.createRouteAssignment([assignment], queueOrder);
        }
      }

      this.toggleAssigningMode();

      const routeNames = assignments.map(a => a.routeName).join(', ');
      this.toast.short(`Routes assigned! '${routeNames}'`);
    } catch (error) {
      this.toast.longer('Error while assigning routes: ' + error);
    }
  }

  deleteRouteAssignment(routeAssignment: RouteAssignment) {
    this.routeAssignmentService
      .deleteRouteAssignment(routeAssignment)
      .then(() => {
        // TODO check if the list is refreshed automatically - it should be
        // this.routeAssignments.splice(this.routeAssignments.indexOf(routeAssignment), 1);
        // this.updateUiItems();
        this.toast.short(`Route '${routeAssignment.routeName}' unassigned.`);
      })
      .catch(error => {
        this.toast.longer(`Route '${routeAssignment.routeName}' could not be unassigned. Error: ${error}`);
      });
  }

  showConfirmationDialog() {
    this.showConfirmDialog = true;
  }

  cancelConfirmation() {
    this.showConfirmDialog = false;
  }

  getCurrentAssignmentsInProgressIfAny(): RouteAssignment[] {
    if (this.selectedVehicle) {
      const vehicleStatus = this.vehiclesStatus.get(this.selectedVehicle.id);
      return vehicleStatus?.routeAssignments;
    }
    return [];
  }

  getRouteNamesFromAssignment(assignments: RouteAssignment[]): string {
    return assignments.map(a => a.routeName).join(', ');
  }

  getRouteNamesFromRouteHierarchyItems(items: RouteHierarchyItem[]): string {
    return items.map(a => a.routeName).join(', ');
  }

  viewAssignment(routeAssignment: RouteAssignment) {
    this.router.navigate(
      [
        `/${RootRoute.MAIN}`,
        MainRoute.INSIGHTS,
        InsightsRoute.SHIFT_DETAIL,
        routeAssignment.shiftId,
        ShiftDetailRoute.ASSIGNMENT,
        routeAssignment.id,
      ],
      {
        queryParamsHandling: 'merge',
      }
    );
  }

  viewRoute(routeAssignment: RouteAssignment) {
    if (!!this.flatRouteItems) {
      const route = this.flatRouteItems.find(item => {
        return item.configId === routeAssignment.configId && item.routeId === routeAssignment.routeId;
      });
      if (!!route) {
        const path = [String(route.configId)];
        if (!!route.path && route.path.length > 0) {
          path.push(...route.path);
        }
        path.push(route.value);
        this.router.navigate([`/${RootRoute.MAIN}`, MainRoute.ROUTE, path.join(':::'), 'route-id', route.routeId], {
          queryParamsHandling: 'merge',
        });
      } else {
        const msg = `Route Configuration not found! Config ID: ${routeAssignment.configId} Route ID: ${routeAssignment.routeId}`;
        console.warn(msg);
        this.toast.longer(msg);
      }
    } else {
      console.warn('Route Configuration not initialized!');
    }
  }

  onDateFilterChanged(dateFilter: DateFilter) {
    this.dateFilter = dateFilter;
    this.updateUiItems();
  }

  onVehicleFilterChanged(vehicleFilter: MultiSelectFilter<number>) {
    this.vehicleFilter = vehicleFilter;
    this.updateUiItems();
  }

  onRouteAssignmentStatusFilterChanged(filter: MultiSelectFilter<string>) {
    this.routeAssignmentStatusFilter = filter;
    this.updateUiItems();
  }

  protected tryToSetAssetIdParamFromState() {
    super.tryToSetAssetIdParamFromState();
    if (!this.assetId) {
      this.assetId = Number(localStorage.getItem(LOCAL_STORAGE_ROUTE_ASSIGNMENT_VEHICLE_ID));
      localStorage.removeItem(LOCAL_STORAGE_ROUTE_ASSIGNMENT_VEHICLE_ID);
    }
  }

  private routeConfigToLeaves(routeConfiguration: RouteConfigurationWithSchema): RouteHierarchyItemWithPath[] {
    return RouteHierarchyItem.getLeaves(routeConfiguration.schema.classification.length, routeConfiguration.configuration.children).map(
      item => {
        item.configId = routeConfiguration.id;
        item.routeName = null;
        return item;
      }
    );
  }

  private routeConfigToHierarchy(routeConfiguration: RouteConfigurationWithSchema): RouteHierarchyItem {
    const items = RouteHierarchyItem.getVisibleItems(routeConfiguration.configuration.children, routeConfiguration.id);
    return {
      routeId: -1,
      routeName: routeConfiguration.name,
      children: items,
      childrenAttribute: '',
      value: String(routeConfiguration.id),
    };
  }

  private updateUiItems() {
    // pre-compute the items assignment status
    const assignmentStatuses = this.calculateRouteAssignmentStatuses();
    if (this.routeAssignments && this.vehicleFilter && this.dateFilter && this.routeAssignmentStatusFilter) {
      this.filteredRouteAssignments = this.routeAssignments.filter(assignment => {
        return (
          (!this.vehicleFilter.elements || this.vehicleFilter.elements.includes(assignment.vehicleId)) &&
          (!this.routeAssignmentStatusFilter.elements ||
            this.routeAssignmentStatusFilter.elements.includes(assignmentStatuses.get(assignment.id))) &&
          (!this.dateFilter.from || this.dateFilter.from.isBefore(assignment.created)) &&
          (!this.dateFilter.to || this.dateFilter.to.isAfter(assignment.created))
        );
      });
      this.segments = this.getUiItemSegments();
    }
  }

  /** Walks the flat item list and folds consecutive same-groupId items into group segments. */
  private getUiItemSegments(): CardSegment[] {
    const result: CardSegment[] = [];
    const list = this.filteredRouteAssignments;

    for (let i = 0; i < list.length; i++) {
      const current = list[i];

      if (!current.groupUuid) {
        result.push({ type: 'single', key: String(current.id), item: this.routeAssignmentToActionMenuItem(current) });
        continue;
      }

      const lastSegment = result[result.length - 1];
      if (lastSegment && lastSegment.type === 'group' && lastSegment.groupUuid === current.groupUuid) {
        lastSegment.items.push(this.routeAssignmentToActionMenuItem(current));
      } else {
        result.push({
          type: 'group',
          key: `group-${current.groupUuid}-${i}`,
          groupUuid: current.groupUuid,
          items: [this.routeAssignmentToActionMenuItem(current)],
        });
      }
    }
    return result;
  }

  private calculateRouteAssignmentStatuses(): Map<number, string> {
    const assignmentStatuses = new Map<number, string>();
    this.routeAssignments?.forEach(assignment => {
      const vehicleStatus = this.vehiclesStatus.get(assignment.vehicleId);
      const isInProgress = this.isInProgress(vehicleStatus, assignment);
      assignmentStatuses.set(assignment.id, this.getAssignmentStatus(assignment, isInProgress, !assignment.shiftId).id);
    });
    return assignmentStatuses;
  }

  private routeAssignmentToActionMenuItem(routeAssignment: RouteAssignment): ActionMenuItem {
    const actions: ActionMenuItemSubMenu[] = [];
    const vehicleStatus = this.vehiclesStatus.get(routeAssignment.vehicleId);
    const isInProgress = this.isInProgress(vehicleStatus, routeAssignment);
    if (!isInProgress && !routeAssignment.completed && !routeAssignment.declined) {
      actions.push(new ActionMenuItemSubMenu('delete', 'Delete', () => this.deleteRouteAssignment(routeAssignment)));
    }
    return new ActionMenuItem(
      `${routeAssignment.configId}_${routeAssignment.routeId}`,
      'route',
      routeAssignment.routeName,
      `${routeAssignment.vehicleName}\n${routeAssignment.driverName ? routeAssignment.driverName : ' '}`,
      this.getAssignmentStatus(routeAssignment, isInProgress, !routeAssignment.shiftId).name,
      null,
      null,
      () => false,
      null,
      () => !routeAssignment.shiftId,
      () => (!routeAssignment.onAssignmentFrom ? this.viewRoute(routeAssignment) : this.viewAssignment(routeAssignment)),
      null,
      null,
      actions
    );
  }

  private prepareAssignmentActions() {
    const assignmentActions: AssignmentAction[] = [
      // new AssignmentAction('Cancel', () => this.toggleAssigningMode())
    ];
    if (this.routeAssignments && this.selectedRoutes && this.selectedRoutes.length > 0 && this.selectedVehicle) {
      /*const vehicleAssignments = this.routeAssignments
        .filter(assignment => assignment.vehicleId === this.selectedVehicle.id && !assignment.completed)
        .sort((a, b) => (a.queueOrder < b.queueOrder ? -1 : 1));*/
      if (this.selectedRoutes.length === 1) {
        assignmentActions.push(new AssignmentAction('Assign', () => this.assignRoute(RouteAssignmentQueue.END)));
      } else {
        assignmentActions.push(
          new AssignmentAction('Assign sequentially', () => this.assignRoute(RouteAssignmentQueue.END)),
          new AssignmentAction('Assign concurrently', () => this.showConfirmationDialog())
        );
      }

      /*if (vehicleAssignments.length === 0) {
        // empty queue, starting immediately
        assignmentActions.push(new AssignmentAction('Assign', () => this.assignRoute(RouteAssignmentQueue.IMMEDIATE)));
      } else if (vehicleAssignments.length === 1) {
        // if the assignment is in progress
        if (!!vehicleAssignments[0].onAssignmentFrom) {
          assignmentActions.push(
            new AssignmentAction('Assign', () => this.assignRoute(RouteAssignmentQueue.END)),
            new AssignmentAction('Start immediately', () => this.showConfirmationDialog())
          );
        } else {
          // one pending assignment - waiting for acceptance
          assignmentActions.push(
            new AssignmentAction('Assign', () => this.assignRoute(RouteAssignmentQueue.END)),
            new AssignmentAction('Make next in queue', () => this.assignRoute(RouteAssignmentQueue.IMMEDIATE))
          );
        }
      } else {
        // queue has at least two assignments
        if (vehicleAssignments[0].onAssignmentFrom) {
          // if the assignment is in progress
          assignmentActions.push(
            new AssignmentAction('Assign', () => this.assignRoute(RouteAssignmentQueue.END)),
            new AssignmentAction('Start immediately', () => this.showConfirmationDialog()),
            new AssignmentAction('Make next in queue', () => this.assignRoute(RouteAssignmentQueue.NEXT))
          );
        } else {
          // none in progress
          assignmentActions.push(
            new AssignmentAction('Assign', () => this.assignRoute(RouteAssignmentQueue.END)),
            new AssignmentAction('Make next in queue', () => this.assignRoute(RouteAssignmentQueue.IMMEDIATE))
          );
        }
      }*/
    }
    this.assignmentActions = assignmentActions;
  }

  private isInProgress(vehicleStatus: VehicleRouteAssignmentStatus, routeAssignment: RouteAssignment) {
    if (vehicleStatus) {
      if (
        vehicleStatus.status === RouteAssignmentStatus.ON_ASSIGNMENT &&
        !!vehicleStatus.routeAssignments.find(a => a.configId === routeAssignment.configId && a.routeId === routeAssignment.routeId)
      ) {
        return true;
      }
    }
    return false;
  }

  private preFillRoute() {
    const routeItemWithPath: RouteHierarchyItemWithPath = this.flatRouteItems?.find(item => {
      return item.configId === this.routeConfigId && item.routeId === this.routeId;
    });

    if (routeItemWithPath) {
      let routeItem: RouteHierarchyItem;
      if (!!routeItemWithPath.path && routeItemWithPath.path.length > 0) {
        routeItem = RouteHierarchyItem.getByPath(
          routeItemWithPath.path,
          this.routeHierarchyItems.find(item => item.value === String(routeItemWithPath.configId))?.children
        )?.children.find(x => x.routeId === routeItemWithPath.routeId);
      } else {
        routeItem = routeItemWithPath;
      }

      this.assigningMode = true;
      this.routeSelectorControl.setValue([routeItem], { emitEvent: false });
      this.selectedRoutes = [routeItem];
      this.onAssignmentFormChange();
    }
  }

  private getAssignmentStatus(routeAssignment: RouteAssignment, inProgress: boolean, offline: boolean): RouteAssignmentStatusItem {
    if (routeAssignment.completed && !routeAssignment.cancelled) {
      return {
        id: 'COMPLETED',
        name: 'Completed',
      };
    } else if (routeAssignment.completed) {
      return {
        id: 'CANCELLED',
        name: 'Cancelled',
      };
    } else if (routeAssignment.declined) {
      return {
        id: 'DECLINED',
        name: 'Declined',
      };
    } else if (inProgress) {
      return {
        id: 'IN_PROGRESS',
        name: 'In progress',
      };
    } else {
      return {
        id: 'PENDING',
        name: `Pending${offline ? ' [offline]' : ''}`,
      };
    }
  }

  onPanelOpened(): void {
    // panelClass="routes-dropdown" is applied to the actual scrollable listbox
    this.panelEl = document.querySelector('.routes-dropdown');
    if (this.panelEl) {
      // capture scroll position before Material's own click handling runs
      this.panelEl.addEventListener('mousedown', this.captureScroll, true);
    }
  }

  private captureScroll = (): void => {
    if (this.panelEl) {
      this.savedScrollTop = this.panelEl.scrollTop;
    }
  };
}
